// Início: saldo, consumo dos últimos dias, status da malha e modelos mais usados.
// Dados reais de /api/client/me (via useMe) e /api/client/usage.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowUpRight, MessageSquare } from 'lucide-react';
import { api, brl, int, type Usage } from '../api';
import { useMeRequired } from '../me';
import '../styles/dashboard.css';

const DAYS = 14;
const DAY_MS = 86_400_000;
const compact = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace('.', ',')}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1).replace('.', ',')}K` : String(n));
const dayLabel = (t: number) => new Date(t).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');

type Day = { start: number; tokens: number };

function buildDays(usage: Usage | null): Day[] {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const first = today.getTime() - (DAYS - 1) * DAY_MS;
  const days: Day[] = Array.from({ length: DAYS }, (_, i) => ({ start: first + i * DAY_MS, tokens: 0 }));
  for (const j of usage?.jobs ?? []) {
    const i = Math.floor((j.created_at - first) / DAY_MS);
    if (i >= 0 && i < DAYS) days[i].tokens += (j.input_tokens ?? 0) + (j.output_tokens ?? 0);
  }
  return days;
}

export function Dashboard() {
  const { me, refresh } = useMeRequired();
  const [usage, setUsage] = useState<Usage | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    void refresh();
    api<Usage>('/api/client/usage').then(setUsage).catch(() => setUsage({ jobs: [], transactions: [] }));
  }, [refresh]);

  const days = useMemo(() => buildDays(usage), [usage]);
  const max = Math.max(1, ...days.map((d) => d.tokens));
  const peak = days.reduce((a, d, i) => (d.tokens > days[a].tokens ? i : a), 0);
  const total = days.reduce((s, d) => s + d.tokens, 0);
  const shown = hover != null ? days[hover] : null;

  const models = useMemo(() => {
    const by = new Map<string, { tokens: number; jobs: number }>();
    for (const j of usage?.jobs ?? []) {
      if (!j.model) continue;
      const cur = by.get(j.model) ?? { tokens: 0, jobs: 0 };
      cur.tokens += (j.input_tokens ?? 0) + (j.output_tokens ?? 0); cur.jobs += 1;
      by.set(j.model, cur);
    }
    return [...by.entries()].sort((a, b) => b[1].tokens - a[1].tokens).slice(0, 5);
  }, [usage]);
  const topTokens = Math.max(1, ...models.map(([, v]) => v.tokens));

  const first = (me.name || me.email).split(/[ @]/)[0];
  const hasCredits = me.credits > 0;

  return (
    <div className="dash">
      <div className="ph">
        <div>
          <span className="eyebrow">Visão geral</span>
          <h1>Olá, {first}</h1>
          <p>Seu saldo, o consumo recente e o estado da malha de inferência.</p>
        </div>
        <div className="actions">
          <Link to="/chat" className="btn primary"><MessageSquare />Abrir chat</Link>
        </div>
      </div>

      <div className="dash-grid">
        {/* ---- saldo ---- */}
        <section className="card accent balance" style={{ gridArea: 'bal' }}>
          <span className="lbl">Saldo</span>
          <strong className={`big ${hasCredits ? '' : 'err'}`}>{brl(me.credits, 2)}</strong>
          <span className="sub">{hasCredits ? 'pronto para usar' : 'sem créditos — peça uma recarga ao operador da rede'}</span>
          <Link to="/uso" className="btn outline-amber"><span>Extrato de créditos</span><ArrowUpRight /></Link>
        </section>

        {/* ---- uso da API ---- */}
        <section className="card usage" style={{ gridArea: 'use' }}>
          <header className="card-h">
            <span className="lbl">Uso da API · {DAYS} dias</span>
            <span className="mono readout" aria-live="polite">
              {shown ? <><b>{dayLabel(shown.start)}</b> · {int(shown.tokens)} tokens</> : <><b>{compact(total)}</b> tokens</>}
            </span>
          </header>
          {usage === null ? <div className="skel chart-skel" /> : total === 0 ? (
            <div className="empty compact"><b>Sem consumo ainda</b><p>Os tokens das suas conversas e chamadas de API aparecem aqui.</p></div>
          ) : (
            <div className="bars" role="img" aria-label={`Tokens por dia nos últimos ${DAYS} dias. Total: ${int(total)}. Pico em ${dayLabel(days[peak].start)}: ${int(days[peak].tokens)}.`} onMouseLeave={() => setHover(null)}>
              {days.map((d, i) => (
                <button
                  key={d.start} type="button" className={`bar ${i === peak || i === DAYS - 1 ? 'hot' : ''} ${hover === i ? 'is-hover' : ''}`}
                  onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                  aria-label={`${dayLabel(d.start)}: ${int(d.tokens)} tokens`}
                >
                  <i style={{ height: `${d.tokens ? Math.max(3, (d.tokens / max) * 100) : 0}%` }} />
                </button>
              ))}
            </div>
          )}
          <div className="axis mono"><span>{dayLabel(days[0].start)}</span><span>hoje</span></div>
        </section>

        {/* ---- números ---- */}
        <div className="card stat hot" style={{ gridArea: 's1' }}><span className="lbl">Tarefas</span><span className="val">{int(me.usage.jobs)}</span><span className="sub">desde o início</span></div>
        <div className="card stat" style={{ gridArea: 's2' }}><span className="lbl">Tokens gerados</span><span className="val">{compact(me.usage.output_tokens)}</span><span className="sub">{int(me.usage.input_tokens)} de entrada</span></div>
        <div className="card stat" style={{ gridArea: 's3' }}><span className="lbl">Total gasto</span><span className="val">{brl(me.usage.charge, 2)}</span><span className="sub">em reais</span></div>

        {/* ---- malha ---- */}
        <section className="card mesh" style={{ gridArea: 'mesh' }}>
          <header className="card-h"><span className="lbl">Status da malha</span></header>
          <div className="mesh-body">
            <Mesh nodes={me.nodes} />
            <div className="mesh-info">
              <span className={`state ${me.nodes > 0 ? 'live' : ''}`}><i />{me.nodes > 0 ? 'Operante' : 'Sem nós'}</span>
              <span className="mono sub">{int(me.nodes)} {me.nodes === 1 ? 'nó ativo' : 'nós ativos'}</span>
              <span className="mono sub">{int(me.models.length)} {me.models.length === 1 ? 'modelo' : 'modelos'} na rede</span>
            </div>
          </div>
        </section>

        {/* ---- modelos ---- */}
        <section className="card models" style={{ gridArea: 'mod' }}>
          <header className="card-h">
            <span className="lbl">{models.length ? 'Modelos recentes' : 'Modelos disponíveis'}</span>
            <Link to="/uso" className="more">ver uso <ArrowUpRight /></Link>
          </header>
          {usage === null ? <div className="skel list-skel" /> : models.length > 0 ? (
            <ul className="mlist">
              {models.map(([name, v]) => (
                <li key={name}>
                  <span className="mono name" title={name}>{name}</span>
                  <span className="mono tok">{int(v.tokens)} tokens</span>
                  <span className="meter"><i style={{ width: `${(v.tokens / topTokens) * 100}%` }} /></span>
                </li>
              ))}
            </ul>
          ) : me.models.length > 0 ? (
            <ul className="mlist">
              {me.models.slice(0, 5).map((name) => (
                <li key={name}><span className="mono name" title={name}>{name}</span><span className="pill ok">disponível</span></li>
              ))}
            </ul>
          ) : (
            <div className="empty compact"><b>Nenhum modelo ativo</b><p>Assim que um nó entrar na malha, os modelos aparecem aqui.</p></div>
          )}
        </section>
      </div>
    </div>
  );
}

/** Malha: nó central + satélites (um por nó ativo, até 14). Posições determinísticas (ângulo áureo). */
function Mesh({ nodes }: { nodes: number }) {
  const n = Math.min(nodes, 14);
  const pts = Array.from({ length: n }, (_, i) => {
    const a = (i * 137.508 * Math.PI) / 180;
    const r = 20 + ((i * 7) % 3) * 9 + i * 0.8;
    return { x: 60 + Math.cos(a) * r * 1.35, y: 50 + Math.sin(a) * r, live: i % 3 === 0 };
  });
  return (
    <svg className="mesh-svg" viewBox="0 0 120 100" role="img" aria-label={`${nodes} nós ativos na malha`}>
      {pts.map((p, i) => <line key={`l${i}`} x1="60" y1="50" x2={p.x} y2={p.y} className={p.live ? 'edge live' : 'edge'} />)}
      {pts.map((p, i) => <circle key={`n${i}`} cx={p.x} cy={p.y} r={p.live ? 3 : 2.2} className={p.live ? 'node live' : 'node'} style={{ animationDelay: `${i * 0.35}s` }} />)}
      <circle cx="60" cy="50" r="7" className="hub" />
      <circle cx="60" cy="50" r="3" className="hub-core" />
    </svg>
  );
}
